import * as analytics from '@origin-digital/reporting-client';
import logger from '../logging';
import { config } from '../../src/utils/config';
import {
  COMP_BUTTON,
  COMP_INPUT_TEXT,
  COMP_RADIO_BUTTON,
  COMP_CHECKBOX,
  COMP_DROPDOWN,
  COMP_ACCORDION,
  COMP_FORM
} from './events';

export type InteractionComponentType =
  | 'button'
  | 'radiobutton'
  | 'inputtext'
  | 'checkbox'
  | 'selectlist'
  | 'form'
  | 'accordion';

export type SolarDisqualificationType = 'postcode' | 'roofType' | 'propertyType' | 'hiddenPostcode';
export type SolarDisqualificationData = {
  postcode?: string;
  productType?: string;
  roofType?: string;
  propertyType?: string;
};

export type SolarFormSubmitData = {
  [key: string]: string | undefined;
};

export class Analytics {
  firstInteractionRun: boolean = false;
  public appName: string;

  constructor(appName: string) {
    this.appName = appName;
    analytics.configAnalytics({
      appName,
      debugToConsole: config.analytics
    });
  }

  interaction(
    type: InteractionComponentType,
    name: string,
    label: string,
    parent?: string,
    data: object = {}
  ) {
    // Trigger the first interaction on any click event if it hasn't already run
    if (!this.firstInteractionRun) this.firstInteraction();
    logger.info('Triggering analytics interaction', { type, name, label });

    analytics.interaction('click', {
      componentType: type,
      id: name,
      parentContainer: parent || name,
      label,
      appName: this.appName,
      ...data
    } as any);
  }

  task(action: 'complete' | 'start' | 'fallout', data: object = {}) {
    analytics.task(this.appName, { action, ...data, appName: this.appName });
  }

  input(name: string, value: string, parent?: string) {
    this.interaction(COMP_INPUT_TEXT, name, value, parent);
  }

  radioSelect(name: string, value: string, parent?: string) {
    this.interaction(COMP_RADIO_BUTTON, name, value, parent);
  }

  dropdownSelect(name: string, value: string, parent?: string) {
    // Trigger the first interaction on any click event if it hasn't already run
    if (!this.firstInteractionRun) this.firstInteraction();
    logger.info('Triggering analytics interaction', {
      COMP_DROPDOWN,
      name,
      label: name
    });

    analytics.interaction('click', {
      appName: this.appName,
      componentType: COMP_DROPDOWN,
      id: name,
      listName: name,
      parentContainer: parent || name,
      listValue: value.toLowerCase()
    } as any);
  }

  formSubmit(name: string, values: SolarFormSubmitData, parent?: string) {
    // Trigger the first interaction on any click event if it hasn't already run
    if (!this.firstInteractionRun) this.firstInteraction();
    logger.info('Triggering analytics interaction', {
      COMP_FORM,
      name,
      label: name
    });

    analytics.interaction('click', {
      appName: this.appName,
      componentType: COMP_FORM,
      id: name,
      parentContainer: parent || name,
      data: values
    } as any);
  }

  buttonClick(name: string, value: string, parent?: string) {
    this.interaction(COMP_BUTTON, name, value, parent);
  }

  checkboxClick(name: string, label: string, value: boolean, parent?: string) {
    const state = value ? 'selected' : 'unselected';
    this.interaction(COMP_CHECKBOX, name, label, parent, { state });
  }

  accordionClick(name: string, label: string, open: boolean, parent?: string) {
    const state = open ? 'open' : 'closed';
    this.interaction(COMP_ACCORDION, name, label, parent, { state });
  }

  pageLoad(uri: string, currentUri = window.location.pathname) {
    analytics.navigation('screen', {
      currentUri,
      friendlyUri: `solar:${this.appName.split('solar-')[1]}:${uri}`,
      referringUri: '' // Optional
    });
  }

  pageLoadAuth(uri: string, currentUri = window.location.pathname) {
    analytics.navigation('screen', {
      currentUri,
      friendlyUri: `${this.appName}`,
      referringUri: '' // Optional
    });
  }

  firstInteraction() {
    if (this.firstInteractionRun) return false;
    this.firstInteractionRun = true;
    this.task('start');
  }

  disqualify(field: SolarDisqualificationType, message: string, data: SolarDisqualificationData) {
    const alertData: {
      appName?: string;
      alertMessage?: string;
      alertCode?: string;
      alertItem?: string;
      solarProduct?: string;
      user?: {
        postcode?: string;
      };
      currentProperty?: {
        roofType?: string;
        propertyType?: string;
      };
    } = {
      appName: this.appName,
      alertMessage: message,
      alertCode: '',
      alertItem: `solar-property-${field.toLowerCase()}`
    };

    if (data.productType) {
      alertData.solarProduct = data.productType.toLowerCase();
    }

    if (data.postcode && !alertData.user) {
      alertData.user = {
        postcode: data.postcode.toLowerCase()
      };
    }

    if ((data.roofType || data.propertyType) && !alertData.currentProperty) {
      alertData.currentProperty = {};
      if (data.roofType) {
        alertData.currentProperty.roofType = data.roofType.toLowerCase();
      }

      if (data.propertyType) {
        alertData.currentProperty.propertyType = data.propertyType.toLowerCase();
      }
    }

    // @ts-ignore New type of alert
    analytics.alert('disqualification', alertData);
  }

  error(message: string, code?: string) {
    analytics.errorForm({
      // @ts-ignore
      appName: this.appName,
      formName: this.appName,
      errorMessage: message,
      errorCode: code
    });
  }

  submitSuccessful(data: object) {
    this.task('complete', data);
  }
}
